import React, { useMemo } from 'react';
import { ContentPage } from './MerchandiserLayout';
import { useNavigate } from 'react-router-dom';
import { useShowErrorMessage, useShowMessage } from '../../../../util/hooks';
import { getPolyglot } from '../../../../i18n';
import { useAdaptableParams } from '../../../../util/hooks/useAdaptableParams';
import WidthRefResize from '../../../../util/renderProps/WidthRefResize';
import { ChartsFilters } from '../../../../components/DeviceManager/Device/Monitoring/components/ChartsFilters/ChartsFilters';
import { Alert, Backdrop, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ComboCharts } from '../../../../components/DeviceManager/Device/Monitoring/components/ComboCharts/ComboCharts';
import { useMetrics } from '../../../../redux/monitoring/hooks/useMetrics';
import moment from 'moment';
import { TIME_FRAME_TYPES, TIME_RANGE_TYPES } from 'stoerk-ui-components';
import { useDevice } from '../../../../redux/commander/hooks/useDevice';
import { useDevicesControlUnits } from '../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { checkDefaultChannels } from '../../../../redux/monitoring/api/util/filter/checkDefaultChannels';
import { getFilterLogicalObjectFromZipDeviceControlUnits } from '../../../../redux/monitoring/api/util/filter/filter.model';
import {
  COMPRESSOR_EVENT,
  SENSOR_F01,
} from '../../../../redux/controlUnit/api/util/constants';
import { useGroupById } from '../../../../redux/groups/hooks/useGroupById';

function MerchandiserMonitoring() {
  const params = useAdaptableParams<{
    groupId: string;
    merchandiserId: string;
    deviceId: string;
  }>();
  const polyglot = getPolyglot();
  const showMessage = useShowMessage();
  const showErrorMessage = useShowErrorMessage();
  const location = {
    state: '',
    key: '',
    pathname: '',
    search: '',
    hash: '',
  };
  const navigate = useNavigate();
  const { deviceId, controlUnitId, groupId } = params;

  const { data: merchandiser } = useGroupById(groupId);

  const controlUnitIdCompressor = useMemo(() => {
    return merchandiser?.composableDevice?.channels?.find(
      (channel) => channel.channel === COMPRESSOR_EVENT
    )?.controlUnitId;
  }, [merchandiser]);

  const controlUnitIdF01 = useMemo(() => {
    return merchandiser?.composableDevice?.channels?.find(
      (channel) => channel.channel === SENSOR_F01
    )?.controlUnitId;
  }, [merchandiser]);

  const [devicesControlUnits] = useDevicesControlUnits();
  const defaultAnalogFilter = checkDefaultChannels(
    getFilterLogicalObjectFromZipDeviceControlUnits(
      devicesControlUnits,
      'channels',
      false
    ),
    false,
    [SENSOR_F01],
    [controlUnitIdF01 ?? ''],
    false
  );
  const defaultDigitalFilter = checkDefaultChannels(
    getFilterLogicalObjectFromZipDeviceControlUnits(
      devicesControlUnits,
      'events',
      false
    ),
    false,
    [COMPRESSOR_EVENT],
    [controlUnitIdCompressor ?? ''],
    false
  );

  /** Init Groups */
  useDevice(deviceId);
  /** Init Monitoring */
  const initialMemoizedTimeFrame = useMemo(() => {
    return {
      start: moment().subtract(6, 'hours'),
      end: moment(),
      timeFrameType: TIME_FRAME_TYPES.LAST_6_HOURS,
      timeRangeType: TIME_RANGE_TYPES.QUICK,
    };
  }, []);

  /** Init Monitoring */
  const {
    timeControls: { timeFrame, setTimeFrame },
    timeControls,
    analogControls,
    digitalControls,
    labStateFilters,
    loading,
    metrics,
    error: fetchError,
  } = useMetrics({
    initialTimeFrame: initialMemoizedTimeFrame,
    defaultAnalogFilter,
    defaultDigitalFilter,
  });

  return (
    <ContentPage
      variant="elevation"
      sx={{
        flex: '1',
        width: '100%',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      <WidthRefResize
        render={(width) => (
          <div id="monitoring-charts">
            {
              <div>
                <ChartsFilters
                  onChangeDatePicker={setTimeFrame}
                  polyglot={polyglot}
                  groupId={groupId}
                  deviceId={deviceId}
                  controlUnitId={controlUnitId}
                  analogControls={analogControls}
                  digitalControls={digitalControls}
                  timeFrame={timeControls.timeFrame}
                  labStateFilters={labStateFilters}
                />
                {loading ? (
                  <Backdrop
                    sx={{
                      backgroundColor: '#ffffffA2',
                      zIndex: 1001,
                    }}
                    open={loading}
                  >
                    <Box
                      display={'flex'}
                      flexDirection={'column'}
                      alignItems={'center'}
                    >
                      <CircularProgress color="inherit" />
                      <Typography>{polyglot.t('general.loading')}</Typography>
                    </Box>
                  </Backdrop>
                ) : fetchError ? (
                  <Alert severity="warning">
                    {polyglot.t('error.unknown')}
                  </Alert>
                ) : (
                  <ComboCharts
                    digitalChannels={metrics.digitalChannels}
                    sensordata={metrics.sensordata}
                    timeControls={timeControls}
                    timeFrame={timeFrame}
                    maxWidth={width - 32}
                    handlingErrorsApi={showErrorMessage}
                    location={location}
                    navigate={navigate}
                    params={params}
                    polyglot={polyglot}
                    showMessage={showMessage}
                    sectionsNavigation={[]}
                  />
                )}
              </div>
            }
          </div>
        )}
      />
    </ContentPage>
  );
}

export default MerchandiserMonitoring;
